export type RewardedAdPlacement = "hint" | "nexus";

export interface AdResult {
  ok: boolean;
  message: string;
}

export interface AdsAdapter {
  platform: "web-mock" | "admob-native";
  initialize(): Promise<AdResult>;
  showSponsoredBanner(placement: "game" | "result" | "shop"): Promise<AdResult>;
  hideSponsoredBanner(): Promise<AdResult>;
  showRewarded(placement: RewardedAdPlacement): Promise<AdResult>;
  showInterstitial(): Promise<AdResult>;
  showPrivacyOptions(): Promise<AdResult>;
}

const TEST_ANDROID_BANNER = "ca-app-pub-3940256099942544/6300978111";
const TEST_ANDROID_INTERSTITIAL = "ca-app-pub-3940256099942544/1033173712";
const TEST_ANDROID_REWARDED = "ca-app-pub-3940256099942544/5224354917";
const TEST_IOS_BANNER = "ca-app-pub-3940256099942544/2934735716";
const TEST_IOS_INTERSTITIAL = "ca-app-pub-3940256099942544/4411468910";
const TEST_IOS_REWARDED = "ca-app-pub-3940256099942544/1712485313";

export function isNativeRuntime() {
  return typeof window !== "undefined" && window.location.protocol === "capacitor:";
}

function envFlag(value: unknown) {
  return String(value ?? "").toLowerCase() === "true";
}

function openPrivacyPolicyFallback() {
  if (typeof window === "undefined") return;
  window.location.assign("/privacy.html");
}

export function getAdsAdapter(): AdsAdapter {
  return isNativeRuntime() ? nativeAdMobAdapter : webMockAdsAdapter;
}

let nativeAdMobInitialize: Promise<AdResult> | undefined;

async function requestIosTrackingAuthorization(AdMob: typeof import("@capacitor-community/admob").AdMob) {
  const { Capacitor } = await import("@capacitor/core");
  if (Capacitor.getPlatform() !== "ios") return;

  const trackingInfo = await AdMob.trackingAuthorizationStatus();
  if (trackingInfo.status === "notDetermined") {
    await AdMob.requestTrackingAuthorization();
  }
}

const webMockAdsAdapter: AdsAdapter = {
  platform: "web-mock",
  async initialize() {
    return { ok: true, message: "Ad test mode ready" };
  },
  async showRewarded() {
    await new Promise((resolve) => window.setTimeout(resolve, 550));
    return { ok: true, message: "Test rewarded ad complete" };
  },
  async showSponsoredBanner() {
    return { ok: true, message: "Sponsored preview ready" };
  },
  async hideSponsoredBanner() {
    return { ok: true, message: "Sponsored preview hidden" };
  },
  async showInterstitial() {
    await new Promise((resolve) => window.setTimeout(resolve, 350));
    return { ok: true, message: "Test interstitial shown" };
  },
  async showPrivacyOptions() {
    openPrivacyPolicyFallback();
    return { ok: true, message: "Privacy page opened" };
  }
};

const nativeAdMobAdapter: AdsAdapter = {
  platform: "admob-native",
  async initialize() {
    nativeAdMobInitialize ??= (async () => {
      const { AdMob, AdmobConsentStatus, MaxAdContentRating } = await import("@capacitor-community/admob");
      await requestIosTrackingAuthorization(AdMob);

      await AdMob.initialize({
        initializeForTesting: envFlag(import.meta.env.VITE_ADMOB_TESTING),
        tagForChildDirectedTreatment: false,
        tagForUnderAgeOfConsent: false,
        maxAdContentRating: MaxAdContentRating.General
      });

      try {
        const consentInfo = await AdMob.requestConsentInfo();
        if (consentInfo.isConsentFormAvailable && consentInfo.status === AdmobConsentStatus.REQUIRED) {
          const updatedConsent = await AdMob.showConsentForm();
          if (!updatedConsent.canRequestAds) {
            return { ok: false, message: "Ads paused until consent allows requests" };
          }
        }
      } catch {
        return { ok: true, message: "Ads ready without consent form" };
      }

      return { ok: true, message: "Ads ready" };
    })().catch((error) => {
      nativeAdMobInitialize = undefined;
      return { ok: false, message: error instanceof Error ? error.message : "Ad setup failed" };
    });

    return nativeAdMobInitialize;
  },
  async showRewarded() {
    try {
      const init = await nativeAdMobAdapter.initialize();
      if (!init.ok) return init;
      const { AdMob } = await import("@capacitor-community/admob");
      const adId = await getAdUnitId("rewarded");
      if (!adId) {
        return { ok: false, message: "Rewarded ad unit is missing" };
      }
      await AdMob.prepareRewardVideoAd({
        adId,
        isTesting: envFlag(import.meta.env.VITE_ADMOB_TESTING),
        immersiveMode: true
      });
      await AdMob.showRewardVideoAd();
      return { ok: true, message: "Reward earned" };
    } catch (error) {
      return { ok: false, message: error instanceof Error ? error.message : "Rewarded ad unavailable" };
    }
  },
  async showSponsoredBanner() {
    try {
      const init = await nativeAdMobAdapter.initialize();
      if (!init.ok) return init;
      const { AdMob, BannerAdPosition, BannerAdSize } = await import("@capacitor-community/admob");
      const adId = await getAdUnitId("banner");
      if (!adId) {
        return { ok: false, message: "Sponsored banner ad unit is missing" };
      }
      await AdMob.showBanner({
        adId,
        adSize: BannerAdSize.ADAPTIVE_BANNER,
        position: BannerAdPosition.BOTTOM_CENTER,
        isTesting: envFlag(import.meta.env.VITE_ADMOB_TESTING),
        margin: 0
      });
      return { ok: true, message: "Sponsored ad loaded" };
    } catch (error) {
      return { ok: false, message: error instanceof Error ? error.message : "Sponsored ad unavailable" };
    }
  },
  async hideSponsoredBanner() {
    try {
      const { AdMob } = await import("@capacitor-community/admob");
      await AdMob.removeBanner();
      return { ok: true, message: "Sponsored ad hidden" };
    } catch (error) {
      return { ok: false, message: error instanceof Error ? error.message : "Sponsored ad could not close" };
    }
  },
  async showInterstitial() {
    try {
      const init = await nativeAdMobAdapter.initialize();
      if (!init.ok) return init;
      const { AdMob } = await import("@capacitor-community/admob");
      const adId = await getAdUnitId("interstitial");
      if (!adId) {
        return { ok: false, message: "Interstitial ad unit is missing" };
      }
      await AdMob.prepareInterstitial({
        adId,
        isTesting: envFlag(import.meta.env.VITE_ADMOB_TESTING),
        immersiveMode: true
      });
      await AdMob.showInterstitial();
      return { ok: true, message: "Ad shown" };
    } catch (error) {
      return { ok: false, message: error instanceof Error ? error.message : "Interstitial unavailable" };
    }
  },
  async showPrivacyOptions() {
    try {
      const { AdMob } = await import("@capacitor-community/admob");
      const init = await nativeAdMobAdapter.initialize();
      if (!init.ok) {
        openPrivacyPolicyFallback();
        return { ok: true, message: "Privacy page opened" };
      }
      const consentInfo = await AdMob.requestConsentInfo({ tagForUnderAgeOfConsent: false });
      if (consentInfo.privacyOptionsRequirementStatus !== "REQUIRED") {
        openPrivacyPolicyFallback();
        return { ok: true, message: "Privacy page opened" };
      }
      await AdMob.showPrivacyOptionsForm();
      return { ok: true, message: "Privacy options opened" };
    } catch (error) {
      openPrivacyPolicyFallback();
      return { ok: true, message: "Privacy page opened" };
    }
  }
};

async function getAdUnitId(kind: "banner" | "rewarded" | "interstitial") {
  const { Capacitor } = await import("@capacitor/core");
  const platform = Capacitor.getPlatform();
  const testing = envFlag(import.meta.env.VITE_ADMOB_TESTING);

  if (kind === "banner") {
    if (platform === "ios") return import.meta.env.VITE_ADMOB_IOS_BANNER_ID || (testing ? TEST_IOS_BANNER : "");
    return import.meta.env.VITE_ADMOB_ANDROID_BANNER_ID || (testing ? TEST_ANDROID_BANNER : "");
  }

  if (kind === "rewarded") {
    if (platform === "ios") return import.meta.env.VITE_ADMOB_IOS_REWARDED_ID || (testing ? TEST_IOS_REWARDED : "");
    return import.meta.env.VITE_ADMOB_ANDROID_REWARDED_ID || (testing ? TEST_ANDROID_REWARDED : "");
  }

  if (platform === "ios") return import.meta.env.VITE_ADMOB_IOS_INTERSTITIAL_ID || (testing ? TEST_IOS_INTERSTITIAL : "");
  return import.meta.env.VITE_ADMOB_ANDROID_INTERSTITIAL_ID || (testing ? TEST_ANDROID_INTERSTITIAL : "");
}
